import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Loader2, GraduationCap, Lock, CheckCircle2, ChevronRight, Clock } from 'lucide-react';
import ModuleDetailPage from '@/pages/ModuleDetailPage';

/** Training inside the sanctuary. One training opens straight into the reader; several get a list. */
export const PlatinumTraining = ({ user }) => {
  const [courses, setCourses] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, done] = await Promise.all([
          axios.get(`${API}/modules?space=platinum_plus`),
          axios.get(`${API}/modules/completed`).catch(() => ({ data: [] })),
        ]);
        const list = (c.data || []).sort((a, b) => a.order - b.order);
        setCourses(list);
        setCompleted(done.data || []);
        if (list.length === 1 && !list[0].is_locked) setOpenId(list[0].id);
      } catch (e) {
        setCourses([]);
      }
    })();
  }, []);

  if (courses === null) {
    return (
      <div className="pp-train-wait" data-testid="platinum-training-loading">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }

  if (openId) {
    const only = courses.length === 1;
    return (
      <div className="pp-skin pp-reader" data-testid="platinum-training-reader">
        <ModuleDetailPage
          user={user}
          moduleId={openId}
          onBack={only ? undefined : () => setOpenId(null)}
        />
      </div>
    );
  }

  return (
    <div className="pp-train" data-testid="platinum-training">
      <header className="pp-train-head">
        <h1><GraduationCap className="w-7 h-7" /> Training</h1>
        <p>Learn the practice of collective intention setting — at the pace it was built for.</p>
      </header>

      {courses.length === 0 ? (
        <div className="pp-train-empty" data-testid="platinum-training-empty">
          <GraduationCap className="w-9 h-9" />
          <h3>The first training is being prepared</h3>
          <p>When it opens, it will appear here — and you'll find it waiting the next time you walk in.</p>
        </div>
      ) : (
        <div className="pp-train-list">
          {courses.map((c) => {
            const done = completed.includes(c.id);
            return (
              <button key={c.id} className="pp-train-card" data-testid={`platinum-training-card-${c.id}`}
                disabled={c.is_locked}
                onClick={() => !c.is_locked && setOpenId(c.id)}>
                <span className="pp-train-icon">
                  {c.is_locked ? <Lock className="w-5 h-5" /> : done ? <CheckCircle2 className="w-5 h-5" /> : <GraduationCap className="w-5 h-5" />}
                </span>
                <span className="pp-train-body">
                  <span className="pp-train-title">{c.title}</span>
                  <span className="pp-train-desc">{c.description}</span>
                  {c.is_locked && c.unlock_date && (
                    <span className="pp-train-note"><Clock className="w-3 h-3" /> Opens {new Date(c.unlock_date).toLocaleDateString()}</span>
                  )}
                  {c.is_locked && !c.unlock_date && (
                    <span className="pp-train-note"><Lock className="w-3 h-3" /> Finish the training before this one</span>
                  )}
                  {done && <span className="pp-train-note pp-done"><CheckCircle2 className="w-3 h-3" /> Completed</span>}
                </span>
                {!c.is_locked && <ChevronRight className="w-5 h-5 pp-train-chev" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlatinumTraining;
